// Recurrence rule and room-warning contracts (PB-04-02, PB-12-03; recurrence & rooms stream).
// No imports from visits.ts: visits.ts uses these schemas.
import { z } from 'zod';

const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');

const End = {
  /** Inclusive local end date; give exactly one of `until` and `count`. */
  until: LocalDate.optional(),
  /** Number of occurrences (excluded dates count; dates that do not exist do not). */
  count: z.number().int().min(1).max(104).optional(),
  exclusions: z.array(LocalDate).max(100).default([]),
};

const MonthlySchema = z.discriminatedUnion('by', [
  z.object({ by: z.literal('day_of_month'), day: z.number().int().min(1).max(31) }),
  z.object({ by: z.literal('nth_weekday'), nth: z.union([z.literal(-1), z.number().int().min(1).max(5)]), weekday: z.number().int().min(1).max(7) }),
]);

export const RecurrenceSchema = z
  .discriminatedUnion('frequency', [
    z.object({ frequency: z.literal('weekly'), weekdays: z.array(z.number().int().min(1).max(7)).min(1).max(7), ...End }),
    z.object({ frequency: z.literal('monthly'), monthly: MonthlySchema, ...End }),
  ])
  .refine((r) => (r.until === undefined) !== (r.count === undefined), { message: 'v.recurrenceEnd', path: ['until'] });
export type RecurrenceDto = z.infer<typeof RecurrenceSchema>;

/** A month where the pattern names a date that does not exist; it is skipped, never moved. */
export interface SkippedOccurrenceDto {
  readonly month: string;
  readonly reason: 'no_such_day' | 'no_such_weekday';
}

export const BOOKING_POLICY_VALUES = ['block', 'warn', 'none'] as const;
export type BookingPolicyValue = (typeof BOOKING_POLICY_VALUES)[number];

/** PB-12-03: an over-capacity booking of a 'warn' destination that a user acknowledged. */
export interface RoomWarningDto {
  readonly kind: 'capacity';
  readonly locationId: string;
  readonly capacity: number;
  readonly count: number;
  readonly acknowledgedBy: { readonly id: string; readonly displayName: string };
  readonly acknowledgedAt: string;
}
